import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import useReducedMotion from '../../hooks/useReducedMotion';
import { fadeUp, maybe } from '../../lib/motionVariants';

/**
 * EmptyState — reusable empty/placeholder card.
 *
 * Props:
 *   icon        — Lucide icon component (required)
 *   iconSize    — number, default 48
 *   title       — string
 *   sub         — string
 *   ctaLabel    — string (if provided, shows a CTA button)
 *   ctaTo       — string (Link path; use either ctaTo OR onCtaClick)
 *   onCtaClick  — function (click handler)
 */
export default function EmptyState({
  icon: Icon,
  iconSize = 48,
  title,
  sub,
  ctaLabel,
  ctaTo,
  onCtaClick,
}) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      variants={maybe(reduced, fadeUp)}
      initial="hidden"
      animate="visible"
      className="card-duo p-10 text-center"
      data-testid="empty-state"
    >
      {Icon && (
        <Icon
          size={iconSize}
          strokeWidth={1.5}
          className="mx-auto text-[var(--duo-stroke-dark,#D1D5DB)] mb-4"
          aria-hidden="true"
        />
      )}

      {title && (
        <h3 className="font-duo-display text-lg text-[var(--text-primary)] mb-2">
          {title}
        </h3>
      )}

      {sub && (
        <p className="text-sm text-gray-400 mb-6">{sub}</p>
      )}

      {ctaLabel && ctaTo && (
        <Link
          to={ctaTo}
          className="btn-duo-primary inline-flex items-center gap-2"
        >
          {ctaLabel}
        </Link>
      )}

      {ctaLabel && onCtaClick && !ctaTo && (
        <button
          onClick={onCtaClick}
          className="btn-duo-primary inline-flex items-center gap-2"
        >
          {ctaLabel}
        </button>
      )}
    </motion.div>
  );
}
